import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { CommonModule, NgClass, NgIf } from "@angular/common";
import { Subject, takeUntil } from "rxjs";
import { Router, RouterLink } from "@angular/router";
import { CdkTableModule } from "@angular/cdk/table";
import { StateAssociationService } from "../../../services/state.association.service";
import { StateAssociation } from "../../../models/state.association.model";
import { StateManagementService } from "../../../services/state-management.service";
import { HttpErrorResponse } from "@angular/common/http";
import { AuthGuard } from "../../../auth/auth.guard";

@Component({
  selector: "app-state-association",
  templateUrl: "./state.associations.component.html",
  standalone: true,
  imports: [FormsModule, NgClass, CommonModule, CdkTableModule],
  styleUrls: ["./state.associations.component.scss"]
})
export class StateAssociationsComponent implements OnInit, OnDestroy {
  private onDestroy$: Subject<void> = new Subject<void>();

  // Table columns
  displayedColumns: string[] = ["state", "memberLevel", "agencyCount"];

  // Data
  stateAssociations: StateAssociation[] = [];
  filteredAssociations: StateAssociation[] = [];
  selectedLevel: string = "";

  // Loading state
  isLoading = false;

  constructor(
    private readonly stateAssociationService: StateAssociationService,
    private stateManagementService: StateManagementService,
    private router: Router,
    private authGuard: AuthGuard
  ) {}

  ngOnInit(): void {
    this.stateManagementService.setPageTitle("State Associations | Momentum Edge");
    this.stateManagementService.pageHeading.set("State Associations");
    if (!this.authGuard.isAdmin()) {
      return;
    }
    this.loadStateAssociations();
  }

  private loadStateAssociations(): void {
    this.isLoading = true;

    this.stateAssociationService
      .getStateAssociations()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (data: StateAssociation[]) => {
          this.stateAssociations = data.sort((a, b) => {
            return (a.state || "").localeCompare(b.state || "");
          });
          this.filteredAssociations = [...this.stateAssociations];
          this.isLoading = false;
        },
        error: (error: HttpErrorResponse) => {
          console.error("Failed to load state associations", error);
          this.isLoading = false;
        }
      });
  }

  filterByLevel(): void {
    if (!this.selectedLevel || this.selectedLevel === "") {
      this.filteredAssociations = [...this.stateAssociations];
      return;
    }
    const level = Number(this.selectedLevel);
    this.filteredAssociations = this.stateAssociations.filter(
      (assoc) => assoc.memberLevel === level
    );
  }

  /**
   * Returns the CSS class for the member level badge
   */
  getMemberLevelClass(level: number): string {
    const classes: Record<number, string> = {
      0: "bg-slate-100 !text-slate-400",
      1: "bg-green-600 text-white",
      2: "bg-amber-800 text-white",
      3: "bg-momentum-deep_purple"
    };
    return classes[level] || "bg-gray-100 text-gray-600";
  }

  /**
   * Returns the display text for the member level
   */
  getMemberLevelText(level: number): string {
    return level === 0 ? "Not a Member" : `Level ${level}`;
  }

  /**
   * Returns the number of agencies for a state association
   */
  getAgencyCount(assoc: StateAssociation): number {
    return assoc.agencies?.length || 0;
  }

  /**
   * Navigates to the detail page for a state association
   */
  navigateToDetail(assoc: StateAssociation): void {
    if (assoc.id) {
      this.router.navigate(["/admin", "state-associations", assoc.id]);
    }
  }

  ngOnDestroy(): void {
    this.stateManagementService.resetHeadingAndTitle();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
